/**
 * GoodsGrowthSeattlePhase - Seattle Metro Solo 商品補充フェーズUIコンポーネント
 *
 * Seattle固有ルール:
 * - Airport に自動で1個配置（最初）
 * - ダイス3個を振る
 * - バッグから3個引いて、ダイス目に対応するCity/Townに配置
 * - 需要過多（3個目）で即敗北
 *
 * Request #2 対応:
 * - 商品選択時にマップ上の該当City/Townをハイライト表示
 * - マップクリックでロケーション選択
 *
 * @see docs/specifications/logic/seattle_metro_solo/phase_09_goods_growth_seattle.md
 */

import { useState, useEffect } from 'react';
import type { SeattleGameState, GoodsColor } from '../../types';
import { useTranslations } from '../../i18n';

export interface GoodsGrowthSeattlePhaseProps {
  G: SeattleGameState;
  isMyTurn: boolean;
  /** 残りのダイス目 */
  remainingDice: number[];
  /** 残りのキューブ */
  remainingCubes: GoodsColor[];
  /** Airport 自動配置の商品（表示用） */
  airportAutoPlacement?: {
    color: GoodsColor;
  };
  /** Airport 満杯によるゲームオーバー状態（Phase開始時に検出） */
  isAirportOverload?: boolean;
  /** マップで選択されたロケーションID（親から受け取る） */
  selectedMapLocation?: string | null;
  onPlaceGoods: (cubeIndex: number, locationId: string) => void;
  onProceed: () => void;
  /** 需要過多によるゲームオーバー */
  onGameOver: () => void;
  /** ハイライト対象ロケーション変更通知（親がマップハイライトに使用） */
  onHighlightLocations?: (availableIds: string[], allIds: string[]) => void;
  /** 選択キャンセル時 */
  onCancelSelection?: () => void;
  /** ダイス目からロケーションIDを取得するヘルパー */
  getLocationsByDice: (diceValues: number[]) => { availableIds: string[]; allIds: string[] };
}

/**
 * Phase 9: 商品補充フェーズUI（Seattle専用）
 */
export function GoodsGrowthSeattlePhase({
  G,
  isMyTurn,
  remainingDice,
  remainingCubes,
  airportAutoPlacement,
  isAirportOverload,
  selectedMapLocation,
  onPlaceGoods,
  onProceed,
  onGameOver,
  onHighlightLocations,
  onCancelSelection,
  getLocationsByDice,
}: GoodsGrowthSeattlePhaseProps) {
  const t = useTranslations();

  // 特別ルール折りたたみの状態
  const [showSpecialRules, setShowSpecialRules] = useState(false);

  // 現在選択中のキューブインデックス
  const [selectedCubeIndex, setSelectedCubeIndex] = useState<number | null>(null);

  // 配置状態を計算
  const allPlaced = remainingCubes.length === 0;
  const placedCount = 3 - remainingCubes.length;

  // キューブ選択時、全残りダイス目に対応するロケーションを取得
  const { availableIds: availableLocations, allIds: allLocations } =
    selectedCubeIndex !== null ? getLocationsByDice(remainingDice) : { availableIds: [], allIds: [] };

  // 需要過多状態: キューブを選択中なのに配置先がない
  const isDemandOverload = selectedCubeIndex !== null && availableLocations.length === 0;

  // キューブ選択時にハイライト対象を親に通知
  useEffect(() => {
    if (selectedCubeIndex !== null) {
      onHighlightLocations?.(availableLocations, allLocations);
    } else {
      onHighlightLocations?.([], []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCubeIndex, remainingDice.length]);  // onHighlightLocations は除外

  // ロケーション名を取得（表示用）
  const getLocationName = (locationId: string): string => {
    const hex = G.map.find(h => h.city?.id === locationId || h.town?.id === locationId);
    return hex?.city?.name || hex?.town?.id || locationId;
  };

  // 選択中ロケーションの商品数を取得（警告表示用）
  const getGoodsCount = (locationId: string): number => {
    const hex = G.map.find(h => h.city?.id === locationId || h.town?.id === locationId);
    return hex?.goods?.length || 0;
  };

  // 配置確定
  const handleConfirmPlacement = () => {
    if (selectedCubeIndex !== null && selectedMapLocation) {
      onPlaceGoods(selectedCubeIndex, selectedMapLocation);
      setSelectedCubeIndex(null);
      // 注: onCancelSelection は呼ばない（resetGoodsPlacement が実行されてしまうため）
      // UI 状態のリセットは onPlaceGoods 内で ActionPanelContainer が行う
    }
  };

  // 選択キャンセル
  const handleCancel = () => {
    setSelectedCubeIndex(null);
    onCancelSelection?.();
  };

  return (
    <div className="flex flex-col h-full">
      {!isMyTurn ? (
        <p className="text-muted-foreground text-sm">{t.common.waiting}</p>
      ) : isAirportOverload ? (
        /* Airport 満杯によるゲームオーバー（Request #5） */
        <div className="flex flex-col h-full">
          <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
            <div className="text-destructive text-lg font-bold mb-2">
              {t.refillGoods?.seattle?.demandOverload || 'Demand Overload!'}
            </div>
            <div className="text-sm text-muted-foreground mb-4">
              {t.refillGoods?.seattle?.airportFull || 'Airport is full! Too many passengers are stranded.'}
            </div>
          </div>
          <div className="flex-shrink-0 pt-2 border-t border-border">
            <button
              className="btn-destructive w-full text-sm py-2"
              onClick={onGameOver}
            >
              {t.refillGoods?.seattle?.gameOver || 'Game Over'}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Airport 自動配置の表示 */}
          {airportAutoPlacement && (
            <div className="flex-shrink-0 text-xs text-muted-foreground border-t pt-2 mb-2">
              <span>{t.refillGoods?.seattle?.airportAuto || 'Airport auto-placement'}:</span>
              <div className={`inline-block w-3 h-3 rounded-sm bg-goods-${airportAutoPlacement.color} ml-1`} />
            </div>
          )}

          {/* 特別ルール折りたたみ */}
          <div className="flex-shrink-0 mb-2">
            <button
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setShowSpecialRules(!showSpecialRules)}
            >
              <span className="text-[10px]">{showSpecialRules ? '▲' : '▼'}</span>
              <span>{t.refillGoods?.seattle?.specialRulesTitle || 'Special Rules'}</span>
            </button>
            {showSpecialRules && (
              <div className="mt-1 p-2 bg-muted/50 rounded text-[10px] text-muted-foreground space-y-1">
                <p>{t.refillGoods?.seattle?.specialRule1}</p>
                <p>{t.refillGoods?.seattle?.specialRule2}</p>
                <p className="text-destructive">{t.refillGoods?.seattle?.specialRule3}</p>
              </div>
            )}
          </div>

          {/* 配置選択UI */}
          {!allPlaced ? (
            <div className="flex-1 min-h-0 flex flex-col space-y-2">
              {/* ダイス目表示 */}
              <div className="flex-shrink-0">
                <div className="text-xs font-medium mb-1">
                  {t.refillGoods?.seattle?.diceResults || 'Dice'}:
                </div>
                <div className="flex gap-1">
                  {remainingDice.map((dice, i) => (
                    <span key={i} className="px-2 py-1 bg-muted rounded text-sm font-bold">
                      {dice}
                    </span>
                  ))}
                </div>
              </div>

              {/* キューブ選択 */}
              <div className="flex-shrink-0">
                <div className="text-xs font-medium mb-1">
                  {t.refillGoods?.seattle?.selectGoods || 'Select goods to place'}:
                </div>
                <div className="flex gap-2">
                  {remainingCubes.map((color, i) => (
                    <button
                      key={i}
                      className={`p-2 rounded border ${
                        selectedCubeIndex === i ? 'border-primary bg-primary/10' : 'border-border'
                      }`}
                      onClick={() => setSelectedCubeIndex(i)}
                    >
                      <div className={`w-5 h-5 rounded-sm bg-goods-${color}`} />
                    </button>
                  ))}
                </div>
              </div>

              {/* 選択中のキューブ情報とマップ選択ガイド */}
              {selectedCubeIndex !== null && (
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {isDemandOverload ? (
                    /* 需要過多: 配置先なし */
                    <div className="text-destructive text-xs p-2 border border-destructive rounded">
                      {t.refillGoods?.seattle?.noAvailableLocation || 'No available location - DEMAND OVERLOAD!'}
                    </div>
                  ) : (
                    /* マップ選択ガイド */
                    <div className="space-y-2">
                      <div className="text-xs text-muted-foreground">
                        {t.refillGoods?.seattle?.clickMapToPlace || 'Click a highlighted location on the map'}
                      </div>

                      {/* 選択されたロケーション表示 */}
                      {selectedMapLocation && (
                        <div className="p-2 rounded border border-primary bg-primary/10">
                          <div className="text-xs font-medium">
                            {t.refillGoods?.seattle?.selectedLocation || 'Selected'}:
                          </div>
                          <div className="flex justify-between items-center mt-1">
                            <span className="font-medium text-sm">{getLocationName(selectedMapLocation)}</span>
                            <span className={`text-[10px] ${getGoodsCount(selectedMapLocation) === 1 ? 'text-warning' : 'text-muted-foreground'}`}>
                              {getGoodsCount(selectedMapLocation)}/2 {getGoodsCount(selectedMapLocation) === 1 && '⚠'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* 全て配置完了 */
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="text-lg font-semibold text-primary">
                  {t.refillGoods?.seattle?.allPlaced || 'All goods placed!'}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {t.refillGoods?.clickToProceed || 'Click to proceed'}
                </div>
              </div>
            </div>
          )}

          {/* アクションボタン（Ph.3 と同様: 横幅いっぱい） */}
          <div className="flex-shrink-0 flex gap-2 pt-2 border-t border-border bg-card mt-2">
            {isDemandOverload ? (
              /* 需要過多: ゲームオーバーボタン */
              <button
                className="btn-destructive flex-1 text-xs py-2"
                onClick={onGameOver}
              >
                {t.refillGoods?.seattle?.gameOver || 'Game Over'}
              </button>
            ) : (
              <>
                {/* Reset: 配置済み商品がある場合のみ有効 */}
                <button
                  className="btn-ghost flex-1 text-xs py-2"
                  onClick={handleCancel}
                  disabled={placedCount === 0}
                >
                  {t.common.reset || 'Reset'}
                </button>
                {/* Confirm: キューブとロケーション選択時のみ有効 */}
                <button
                  className="btn-primary flex-1 text-xs py-2"
                  onClick={handleConfirmPlacement}
                  disabled={selectedCubeIndex === null || !selectedMapLocation}
                >
                  {t.common.confirm}
                </button>
                {/* Finish: 全配置完了時のみ有効 */}
                <button
                  className="btn-secondary flex-1 text-xs py-2"
                  onClick={onProceed}
                  disabled={!allPlaced}
                >
                  {t.common.finish}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default GoodsGrowthSeattlePhase;
